import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { api } from '../config/api';

interface ContainerLine {
  id: number;
  stock_code: string;
  po_number: string | null;
  rolls_per_pallet: number;
  sheets: number;
  metres: number;
  prepopulated_qty: number;
  net_weight_tonnes: number;
}

interface POSummary {
  po_number: string;
  line_count: number;
  unique_stocks: number;
}

interface Container {
  id: number;
  container_no: string;
  po_summary?: POSummary[];
  primary_po_number?: string | null;
  is_shared?: boolean;
  import_batch_id?: string | null;
  batch_description?: string | null;
  batch_is_merged?: boolean;
  batch_file_count?: number;
}

interface ContainerDetailModalProps {
  open: boolean;
  container: Container | null;
  onClose: () => void;
  onSaved?: () => void;
}

const ContainerDetailModal: React.FC<ContainerDetailModalProps> = ({
  open,
  container,
  onClose,
  onSaved
}) => {
  const [containerLines, setContainerLines] = useState<ContainerLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLines, setSelectedLines] = useState<Set<number>>(new Set());
  const [bulkPO, setBulkPO] = useState('');
  const [editedLines, setEditedLines] = useState<Map<number, string | null>>(new Map());
  const [validationWarnings, setValidationWarnings] = useState<any[]>([]);

  // Load container lines
  useEffect(() => {
    if (open && container) {
      loadContainerLines();
    } else {
      // Reset state when modal closes
      setContainerLines([]);
      setSelectedLines(new Set());
      setEditedLines(new Map());
      setValidationWarnings([]);
      setError(null);
    }
  }, [open, container]);

  const loadContainerLines = async () => {
    if (!container) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/containers/${container.id}/lines`);

      if (response.data.success) {
        setContainerLines(response.data.lines);
      } else {
        setError('Failed to load container lines');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load container lines');
      console.error('Error loading container lines:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLine = (lineId: number) => {
    const newSelected = new Set(selectedLines);
    if (newSelected.has(lineId)) {
      newSelected.delete(lineId);
    } else {
      newSelected.add(lineId);
    }
    setSelectedLines(newSelected);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedLines(new Set(containerLines.map(line => line.id)));
    } else {
      setSelectedLines(new Set());
    }
  };

  const handleLinePOChange = (lineId: number, newPO: string | null) => {
    const newEdited = new Map(editedLines);
    if (newPO === '') {
      newEdited.set(lineId, null);
    } else {
      newEdited.set(lineId, newPO);
    }
    setEditedLines(newEdited);
  };

  const handleBulkAssign = () => {
    if (!bulkPO || selectedLines.size === 0) return;

    const newEdited = new Map(editedLines);
    selectedLines.forEach(lineId => {
      newEdited.set(lineId, bulkPO === 'null' ? null : bulkPO);
    });
    setEditedLines(newEdited);
    setBulkPO('');
    setSelectedLines(new Set());
  };

  const handleSave = async (force: boolean = false) => {
    if (!container || editedLines.size === 0) return;

    try {
      setSaving(true);
      setError(null);
      setValidationWarnings([]);

      const updates = Array.from(editedLines.entries()).map(([line_id, po_number]) => ({
        line_id,
        po_number
      }));

      const response = await api.put(
        `/containers/${container.id}/lines/update-pos`,
        { updates, force }
      );

      if (response.data.success) {
        // Success
        if (response.data.validation_warnings && response.data.validation_warnings.length > 0) {
          setValidationWarnings(response.data.validation_warnings);
        }

        // Reload container lines
        await loadContainerLines();
        setEditedLines(new Map());

        if (onSaved) {
          onSaved();
        }

        if (!force && response.data.validation_warnings?.length === 0) {
          onClose();
        }
      }
    } catch (err: any) {
      if (err.response?.data?.validation_errors) {
        // Show validation errors with option to force
        setValidationWarnings(err.response.data.validation_errors);
        setError(err.response.data.message);
      } else {
        setError(err.response?.data?.error || 'Failed to save PO assignments');
      }
      console.error('Error saving PO assignments:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!container) return null;

  // Get available POs from container's po_summary or all unique POs from lines
  const availablePOs = container.po_summary?.map(ps => ps.po_number) ||
                       Array.from(new Set(containerLines.map(line => line.po_number).filter(Boolean))) as string[];

  const hasEdits = editedLines.size > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Container {container.container_no} - Line Management
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Batch Information Section */}
        {container.import_batch_id && (
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Import Batch Information
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={container.batch_description || `Batch ${container.import_batch_id.substring(0, 8)}`}
                color="info"
                variant="outlined"
              />
              {container.batch_is_merged && (
                <Chip label="Merged Batch" size="small" color="secondary" />
              )}
              {container.batch_file_count && container.batch_file_count > 1 && (
                <Chip
                  label={`${container.batch_file_count} files`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        )}

        {/* PO Breakdown Section */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Purchase Orders in Container
          </Typography>

          {container.po_summary && container.po_summary.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {container.po_summary.map((po) => (
                <Chip
                  key={po.po_number}
                  label={`PO ${po.po_number}: ${po.line_count} lines, ${po.unique_stocks} stocks`}
                  color={po.po_number === container.primary_po_number ? 'primary' : 'default'}
                  variant={po.po_number === container.primary_po_number ? 'filled' : 'outlined'}
                  icon={po.po_number === container.primary_po_number ? <CheckCircleIcon /> : undefined}
                />
              ))}
            </Stack>
          ) : (
            <Alert severity="warning">No PO assignments yet</Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Bulk Actions */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Bulk Actions ({selectedLines.size} selected)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Assign to PO</InputLabel>
              <Select
                value={bulkPO}
                onChange={(e) => setBulkPO(e.target.value)}
                label="Assign to PO"
                disabled={selectedLines.size === 0}
              >
                <MenuItem value=""><em>Choose PO</em></MenuItem>
                {availablePOs.map((po) => (
                  <MenuItem key={po} value={po}>PO {po}</MenuItem>
                ))}
                <MenuItem value="null">Unassigned (NULL)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleBulkAssign}
              disabled={!bulkPO || selectedLines.size === 0}
            >
              Apply to Selected
            </Button>
          </Stack>
        </Box>

        {/* Error/Warning Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {validationWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Stock Code Validation Warnings:
            </Typography>
            {validationWarnings.map((warning, idx) => (
              <Typography key={idx} variant="body2">
                • Line {warning.line_id}: {warning.error}
              </Typography>
            ))}
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => handleSave(true)}
              sx={{ mt: 1 }}
              startIcon={<WarningIcon />}
            >
              Force Save Anyway
            </Button>
          </Alert>
        )}

        {/* Container Lines Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedLines.size === containerLines.length && containerLines.length > 0}
                      indeterminate={selectedLines.size > 0 && selectedLines.size < containerLines.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Line ID</TableCell>
                  <TableCell>Stock Code</TableCell>
                  <TableCell>Current PO</TableCell>
                  <TableCell>New PO Assignment</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Sheets</TableCell>
                  <TableCell align="right">Metres</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {containerLines.map((line) => {
                  const newPO = editedLines.get(line.id);
                  const hasEdit = editedLines.has(line.id);
                  const currentPO = hasEdit ? newPO : line.po_number;

                  return (
                    <TableRow
                      key={line.id}
                      selected={selectedLines.has(line.id)}
                      sx={{ backgroundColor: hasEdit ? '#fff8e1' : undefined }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLines.has(line.id)}
                          onChange={() => handleSelectLine(line.id)}
                        />
                      </TableCell>
                      <TableCell>{line.id}</TableCell>
                      <TableCell>{line.stock_code}</TableCell>
                      <TableCell>
                        {line.po_number ? (
                          <Chip label={line.po_number} size="small" />
                        ) : (
                          <Chip label="Unassigned" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={currentPO || ''}
                            onChange={(e) => handleLinePOChange(line.id, e.target.value || null)}
                            displayEmpty
                          >
                            <MenuItem value=""><em>Unassigned</em></MenuItem>
                            {availablePOs.map((po) => (
                              <MenuItem key={po} value={po}>PO {po}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">{line.prepopulated_qty || line.rolls_per_pallet}</TableCell>
                      <TableCell align="right">{line.sheets?.toLocaleString() || '-'}</TableCell>
                      <TableCell align="right">{line.metres?.toLocaleString() || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {containerLines.length === 0 && !loading && (
          <Alert severity="info">No lines in this container yet</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => handleSave(false)}
          variant="contained"
          disabled={!hasEdits || saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : `Save Changes${hasEdits ? ` (${editedLines.size})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContainerDetailModal;
